// Recurring Appointments Service for BookAPT
// Handles creation and management of recurring appointments

import { Platform } from 'react-native';

export interface RecurringAppointment {
  id: string;
  customerId: string;
  businessId: string;
  serviceId: string;
  serviceName: string;
  duration: number;
  price: number;
  frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'yearly';
  dayOfWeek?: number; // 0 = Sunday, 1 = Monday, etc.
  dayOfMonth?: number; // For monthly/yearly
  startDate: string;
  endDate?: string;
  nextBookingDate: string;
  isActive: boolean;
  totalBookings: number;
  completedBookings: number;
  cancelledBookings: number;
  customerData: {
    name: string;
    phone: string;
    email: string;
    preferences: string[];
  };
  businessData: {
    name: string;
    address: string;
    phone: string;
    email: string;
  };
  serviceData: {
    name: string;
    duration: number;
    price: number | null;
    description: string;
    requirements: string[];
  };
  createdAt: string;
  lastModified: string;
}

export interface RecurringBookingResult {
  success: boolean;
  recurringAppointmentId?: string;
  individualAppointments?: string[];
  error?: string;
}

export interface RecurringPattern {
  frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'yearly';
  interval: number;
  daysOfWeek?: number[];
  dayOfMonth?: number;
  endDate?: string;
  maxOccurrences?: number;
}

class RecurringAppointmentsService {
  private readonly RECURRING_APPOINTMENTS_KEY = 'recurring_appointments';
  private readonly RECURRING_PATTERNS_KEY = 'recurring_patterns';

  // Create recurring appointment
  async createRecurringAppointment(
    appointmentData: {
      customerId: string;
      businessId: string;
      serviceId: string;
      serviceName: string;
      duration: number;
      price: number | null;
      frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'yearly';
      dayOfWeek?: number;
      dayOfMonth?: number;
      startDate: string;
      endDate?: string;
      customerData: {
        name: string;
        phone: string;
        email: string;
        preferences: string[];
      };
      businessData: {
        name: string;
        address: string;
        phone: string;
        email: string;
      };
      serviceData: {
        name: string;
        duration: number;
        price: number | null;
        description: string;
        requirements: string[];
      };
    }
  ): Promise<RecurringBookingResult> {
    try {
      const priceStored = appointmentData.price ?? 0;
      const serviceDataStored = {
        ...appointmentData.serviceData,
        price: appointmentData.serviceData.price ?? 0,
      };
      const recurringAppointment: RecurringAppointment = {
        id: `recurring_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        customerId: appointmentData.customerId,
        businessId: appointmentData.businessId,
        serviceId: appointmentData.serviceId,
        serviceName: appointmentData.serviceName,
        duration: appointmentData.duration,
        price: priceStored,
        frequency: appointmentData.frequency,
        dayOfWeek: appointmentData.dayOfWeek,
        dayOfMonth: appointmentData.dayOfMonth,
        startDate: appointmentData.startDate,
        endDate: appointmentData.endDate,
        nextBookingDate: this.calculateNextBookingDate(
          appointmentData.startDate,
          appointmentData.frequency,
          appointmentData.dayOfWeek,
          appointmentData.dayOfMonth
        ),
        isActive: true,
        totalBookings: 0,
        completedBookings: 0,
        cancelledBookings: 0,
        customerData: appointmentData.customerData,
        businessData: appointmentData.businessData,
        serviceData: serviceDataStored,
        createdAt: new Date().toISOString(),
        lastModified: new Date().toISOString()
      };

      // Save recurring appointment
      await this.saveRecurringAppointment(recurringAppointment);

      // Generate individual appointments for the next 3 months
      const individualAppointments = await this.generateIndividualAppointments(
        recurringAppointment,
        3 // Generate 3 months ahead
      );

      return {
        success: true,
        recurringAppointmentId: recurringAppointment.id,
        individualAppointments
      };
    } catch (error) {
      console.error('Error creating recurring appointment:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create recurring appointment'
      };
    }
  }

  // Generate individual appointments from recurring pattern
  private async generateIndividualAppointments(
    recurringAppointment: RecurringAppointment,
    monthsAhead: number
  ): Promise<string[]> {
    try {
      const appointments: string[] = [];
      const startDate = new Date(recurringAppointment.startDate);
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + monthsAhead);

      let currentDate = new Date(startDate);
      let appointmentCount = 0;

      while (currentDate <= endDate && appointmentCount < 50) { // Max 50 appointments
        const appointmentDate = this.getNextAppointmentDate(
          currentDate,
          recurringAppointment.frequency,
          recurringAppointment.dayOfWeek,
          recurringAppointment.dayOfMonth
        );

        if (appointmentDate > endDate) break;

        // Create individual appointment
        const individualAppointment = {
          id: `apt_${recurringAppointment.id}_${appointmentCount}`,
          customerId: recurringAppointment.customerId,
          businessId: recurringAppointment.businessId,
          serviceId: recurringAppointment.serviceId,
          serviceName: recurringAppointment.serviceName,
          duration: recurringAppointment.duration,
          price: recurringAppointment.price,
          appointmentDate: appointmentDate.toISOString().split('T')[0],
          appointmentTime: this.getAppointmentTime(recurringAppointment.startDate),
          status: 'pending',
          isRecurring: true,
          recurringAppointmentId: recurringAppointment.id,
          customerData: recurringAppointment.customerData,
          businessData: recurringAppointment.businessData,
          serviceData: recurringAppointment.serviceData,
          createdAt: new Date().toISOString()
        };

        // Save individual appointment
        await this.saveIndividualAppointment(individualAppointment);
        appointments.push(individualAppointment.id);

        // Update current date for next iteration
        currentDate = new Date(appointmentDate);
        currentDate.setDate(currentDate.getDate() + 1);
        appointmentCount++;
      }

      return appointments;
    } catch (error) {
      console.error('Error generating individual appointments:', error);
      return [];
    }
  }

  // Get recurring appointments for user
  async getRecurringAppointments(customerId: string): Promise<RecurringAppointment[]> {
    try {
      const recurringAppointments = await this.getAllRecurringAppointments();
      return recurringAppointments.filter(apt => apt.customerId === customerId);
    } catch (error) {
      console.error('Error getting recurring appointments:', error);
      return [];
    }
  }

  // Get recurring appointments for business
  async getBusinessRecurringAppointments(businessId: string): Promise<RecurringAppointment[]> {
    try {
      const recurringAppointments = await this.getAllRecurringAppointments();
      return recurringAppointments.filter(apt => apt.businessId === businessId);
    } catch (error) {
      console.error('Error getting business recurring appointments:', error);
      return [];
    }
  }

  // Update recurring appointment
  async updateRecurringAppointment(
    recurringAppointmentId: string,
    updates: Partial<RecurringAppointment>
  ): Promise<boolean> {
    try {
      const recurringAppointments = await this.getAllRecurringAppointments();
      const index = recurringAppointments.findIndex(apt => apt.id === recurringAppointmentId);
      
      if (index === -1) {
        return false;
      }

      recurringAppointments[index] = {
        ...recurringAppointments[index],
        ...updates,
        lastModified: new Date().toISOString()
      };

      await this.saveAllRecurringAppointments(recurringAppointments);
      return true;
    } catch (error) {
      console.error('Error updating recurring appointment:', error);
      return false;
    }
  }

  // Cancel recurring appointment
  async cancelRecurringAppointment(
    recurringAppointmentId: string,
    cancelFutureOnly: boolean = false
  ): Promise<boolean> {
    try {
      const recurringAppointments = await this.getAllRecurringAppointments();
      const recurringAppointment = recurringAppointments.find(apt => apt.id === recurringAppointmentId);
      
      if (!recurringAppointment) {
        return false;
      }

      if (cancelFutureOnly) {
        // Cancel future appointments but keep past ones
        recurringAppointment.isActive = false;
        recurringAppointment.lastModified = new Date().toISOString();
      } else {
        // Cancel all future appointments
        recurringAppointment.isActive = false;
        recurringAppointment.lastModified = new Date().toISOString();
      }

      await this.updateRecurringAppointment(recurringAppointmentId, recurringAppointment);
      return true;
    } catch (error) {
      console.error('Error cancelling recurring appointment:', error);
      return false;
    }
  }

  // Get next booking date for recurring appointment
  async getNextBookingDate(recurringAppointmentId: string): Promise<string | null> {
    try {
      const recurringAppointments = await this.getAllRecurringAppointments();
      const recurringAppointment = recurringAppointments.find(apt => apt.id === recurringAppointmentId);
      
      if (!recurringAppointment || !recurringAppointment.isActive) {
        return null;
      }

      return recurringAppointment.nextBookingDate;
    } catch (error) {
      console.error('Error getting next booking date:', error);
      return null;
    }
  }

  // Mark recurring appointment as completed
  async markRecurringAppointmentCompleted(
    recurringAppointmentId: string,
    appointmentId: string
  ): Promise<boolean> {
    try {
      const recurringAppointments = await this.getAllRecurringAppointments();
      const recurringAppointment = recurringAppointments.find(apt => apt.id === recurringAppointmentId);
      
      if (!recurringAppointment) {
        return false;
      }

      recurringAppointment.completedBookings += 1;
      recurringAppointment.totalBookings += 1;
      recurringAppointment.nextBookingDate = this.calculateNextBookingDate(
        recurringAppointment.nextBookingDate,
        recurringAppointment.frequency,
        recurringAppointment.dayOfWeek,
        recurringAppointment.dayOfMonth
      );
      recurringAppointment.lastModified = new Date().toISOString();

      await this.updateRecurringAppointment(recurringAppointmentId, recurringAppointment);
      return true;
    } catch (error) {
      console.error('Error marking recurring appointment completed:', error);
      return false;
    }
  }

  // Get recurring appointment analytics
  async getRecurringAnalytics(businessId: string): Promise<{
    totalRecurringAppointments: number;
    activeRecurringAppointments: number;
    totalBookings: number;
    completedBookings: number;
    cancelledBookings: number;
    averageFrequency: string;
    mostPopularFrequency: string;
    revenue: number;
  }> {
    try {
      const recurringAppointments = await this.getBusinessRecurringAppointments(businessId);
      
      const analytics = {
        totalRecurringAppointments: recurringAppointments.length,
        activeRecurringAppointments: recurringAppointments.filter(apt => apt.isActive).length,
        totalBookings: recurringAppointments.reduce((sum, apt) => sum + apt.totalBookings, 0),
        completedBookings: recurringAppointments.reduce((sum, apt) => sum + apt.completedBookings, 0),
        cancelledBookings: recurringAppointments.reduce((sum, apt) => sum + apt.cancelledBookings, 0),
        averageFrequency: this.calculateAverageFrequency(recurringAppointments),
        mostPopularFrequency: this.getMostPopularFrequency(recurringAppointments),
        revenue: recurringAppointments.reduce((sum, apt) => sum + (apt.price * apt.completedBookings), 0)
      };

      return analytics;
    } catch (error) {
      console.error('Error getting recurring analytics:', error);
      return {
        totalRecurringAppointments: 0,
        activeRecurringAppointments: 0,
        totalBookings: 0,
        completedBookings: 0,
        cancelledBookings: 0,
        averageFrequency: 'weekly',
        mostPopularFrequency: 'weekly',
        revenue: 0
      };
    }
  }

  // Helper methods
  private calculateNextBookingDate(
    startDate: string,
    frequency: string,
    dayOfWeek?: number,
    dayOfMonth?: number
  ): string {
    const date = new Date(startDate);
    
    switch (frequency) {
      case 'daily':
        date.setDate(date.getDate() + 1);
        break;
      case 'weekly':
        date.setDate(date.getDate() + 7);
        break;
      case 'biweekly':
        date.setDate(date.getDate() + 14);
        break;
      case 'monthly':
        date.setMonth(date.getMonth() + 1);
        break;
      case 'yearly':
        date.setFullYear(date.getFullYear() + 1);
        break;
    }
    
    return date.toISOString().split('T')[0];
  }

  private getNextAppointmentDate(
    currentDate: Date,
    frequency: string,
    dayOfWeek?: number,
    dayOfMonth?: number
  ): Date {
    const date = new Date(currentDate);
    
    switch (frequency) {
      case 'daily':
        return date;
      case 'weekly':
        if (dayOfWeek !== undefined) {
          const daysUntilTarget = (dayOfWeek - date.getDay() + 7) % 7;
          date.setDate(date.getDate() + daysUntilTarget);
        }
        return date;
      case 'biweekly':
        if (dayOfWeek !== undefined) {
          const daysUntilTarget = (dayOfWeek - date.getDay() + 7) % 7;
          date.setDate(date.getDate() + daysUntilTarget);
        }
        return date;
      case 'monthly':
        if (dayOfMonth !== undefined) {
          date.setDate(dayOfMonth);
        }
        return date;
      case 'yearly':
        if (dayOfMonth !== undefined) {
          date.setDate(dayOfMonth);
        }
        return date;
      default:
        return date;
    }
  }

  private getAppointmentTime(startDate: string): string {
    const date = new Date(startDate);
    return date.toTimeString().split(' ')[0].substring(0, 5);
  }

  private calculateAverageFrequency(recurringAppointments: RecurringAppointment[]): string {
    if (recurringAppointments.length === 0) return 'weekly';
    
    const frequencyCounts: { [key: string]: number } = {};
    recurringAppointments.forEach(apt => {
      frequencyCounts[apt.frequency] = (frequencyCounts[apt.frequency] || 0) + 1;
    });
    
    const mostCommon = Object.keys(frequencyCounts).reduce((a, b) => 
      frequencyCounts[a] > frequencyCounts[b] ? a : b
    );
    
    return mostCommon;
  }

  private getMostPopularFrequency(recurringAppointments: RecurringAppointment[]): string {
    return this.calculateAverageFrequency(recurringAppointments);
  }

  private async saveRecurringAppointment(recurringAppointment: RecurringAppointment): Promise<void> {
    try {
      const recurringAppointments = await this.getAllRecurringAppointments();
      recurringAppointments.push(recurringAppointment);
      await this.saveAllRecurringAppointments(recurringAppointments);
    } catch (error) {
      console.error('Error saving recurring appointment:', error);
    }
  }

  private async saveIndividualAppointment(appointment: any): Promise<void> {
    try {
      // This would integrate with the existing appointment manager
      const { appointmentManager } = await import('../utils/appointmentManager');
      await appointmentManager.saveAppointment(appointment);
    } catch (error) {
      console.error('Error saving individual appointment:', error);
    }
  }

  private async getAllRecurringAppointments(): Promise<RecurringAppointment[]> {
    try {
      // Try to load from Firestore first (most up-to-date)
      let loadedAppointments: RecurringAppointment[] = [];
      let loadedFromFirestore = false;
      
      if (Platform.OS === 'web') {
        try {
          const { collection, getDocs } = await import('firebase/firestore');
          const { getWebDb } = await import('./unifiedAuthService');
          const db = getWebDb();
          
          if (db) {
            const appointmentsSnapshot = await getDocs(collection(db, 'recurring_appointments'));
            loadedAppointments = appointmentsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            } as RecurringAppointment));
            loadedFromFirestore = true;
            console.log(`✅ RecurringAppointmentsService: Loaded ${loadedAppointments.length} recurring appointments from Firestore`);
            
            // Sync to local storage
            const { SimpleStorage } = await import('../utils/simpleStorage');
            await SimpleStorage.setItem(this.RECURRING_APPOINTMENTS_KEY, JSON.stringify(loadedAppointments));
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  RecurringAppointmentsService: Could not load from Firestore, trying local storage:', firestoreError);
        }
      }
      
      // Fallback to local storage if Firestore didn't work
      if (!loadedFromFirestore) {
      const { SimpleStorage } = await import('../utils/simpleStorage');
      const appointmentsJson = await SimpleStorage.getItem(this.RECURRING_APPOINTMENTS_KEY);
        if (appointmentsJson) {
          loadedAppointments = JSON.parse(appointmentsJson);
          console.log(`📋 RecurringAppointmentsService: Loaded ${loadedAppointments.length} recurring appointments from local storage`);
        }
      }
      
      return loadedAppointments;
    } catch (error) {
      console.error('Error getting all recurring appointments:', error);
      return [];
    }
  }

  private async saveAllRecurringAppointments(appointments: RecurringAppointment[]): Promise<void> {
    try {
      // Save to local storage (for offline access)
      const { SimpleStorage } = await import('../utils/simpleStorage');
      await SimpleStorage.setItem(this.RECURRING_APPOINTMENTS_KEY, JSON.stringify(appointments));
      
      // Also save to Firestore (for cross-browser/device access)
      if (Platform.OS === 'web') {
        try {
          const { doc, setDoc } = await import('firebase/firestore');
          const { getWebDb } = await import('./unifiedAuthService');
          const db = getWebDb();
          
          if (db) {
            // Save each recurring appointment as a separate document
            for (const appointment of appointments) {
              await setDoc(doc(db, 'recurring_appointments', appointment.id), {
                ...appointment,
                updatedAt: new Date().toISOString(),
              });
            }
            console.log(`✅ RecurringAppointmentsService: ${appointments.length} recurring appointments saved to Firestore`);
          }
        } catch (firestoreError: any) {
          console.error('❌ RecurringAppointmentsService: Error saving to Firestore:', firestoreError);
          console.warn('⚠️  Recurring appointments saved locally but not in Firestore');
          // Don't throw - local save succeeded
        }
      }
    } catch (error) {
      console.error('Error saving all recurring appointments:', error);
    }
  }
}

export const recurringAppointmentsService = new RecurringAppointmentsService();
export default RecurringAppointmentsService;



